import classNames from "classnames";
import fileDownload from "js-file-download";
import React, { SyntheticEvent, useCallback, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { DownloadIcon } from "../../../icons";
import {
    getChartOfAccountsCsv,
    getChartOfAccountsXlsx,
} from "../../../lib/chartOfAccounts";
import { useAccountingTabContext } from "../../accounting/useAccountingTab";
import { CustomButtonToggle } from "../../general/CustomToggle/CustomToggle";
import { Loader } from "../../general/Loader";
import "./ChartOfAccountsExport.scss";

export enum ChartOfAccountsExportFormat {
    CSV = "csv",
    XLSX = "xlsx",
}

interface ExportFunctionParams {
    format: ChartOfAccountsExportFormat;
    isLoading: boolean;
    loadingSetter: React.Dispatch<React.SetStateAction<boolean>>;
}

const ExportDropDownItem: React.FC<{
    exportFn: (data: ExportFunctionParams) => Promise<void>;
    format: ChartOfAccountsExportFormat;
    children: React.ReactNode;
}> = ({ exportFn, format, children }) => {
    const [isLoading, setIsLoading] = useState(false);
    return (
        <Dropdown.Item
            onClick={() =>
                exportFn({
                    format,
                    isLoading,
                    loadingSetter: setIsLoading,
                })
            }
        >
            {isLoading ? (
                <span className="chart-of-accounts-export__icon">
                    <Loader inline={true} />
                </span>
            ) : null}
            {children}
        </Dropdown.Item>
    );
};

export const ChartOfAccountsExport: React.FC = () => {
    const [opened, setOpened] = useState(false);
    const { selectedEntity } = useAccountingTabContext();

    const exportChartOfAccounts = useCallback(
        async ({ format, isLoading, loadingSetter }: ExportFunctionParams) => {
            if (isLoading || !selectedEntity) {
                return;
            }

            loadingSetter(true);

            try {
                const fileName = `${selectedEntity.name} - Chart of Accounts.${format}`;

                if (format === ChartOfAccountsExportFormat.XLSX) {
                    const response = await getChartOfAccountsXlsx(
                        selectedEntity.id,
                    );
                    fileDownload(
                        response,
                        fileName,
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    );
                } else {
                    const response = await getChartOfAccountsCsv(
                        selectedEntity.id,
                    );
                    fileDownload(response, fileName, "text/csv");
                }
            } finally {
                loadingSetter(false);
            }
        },
        [selectedEntity],
    );

    const onDropdownToggle = useCallback(
        (isOpen: boolean, event: SyntheticEvent<Dropdown>, metadata: any) => {
            if (metadata.source === "select") {
                setOpened(true);
            } else {
                setOpened(isOpen);
            }
        },
        [],
    );

    return (
        <Dropdown show={opened} onToggle={onDropdownToggle}>
            <Dropdown.Toggle
                id="chart-of-accounts-export"
                as={CustomButtonToggle}
                variant="secondary"
                className={classNames({
                    active: opened,
                })}
            >
                <DownloadIcon />
            </Dropdown.Toggle>

            <Dropdown.Menu align="right">
                <ExportDropDownItem
                    exportFn={exportChartOfAccounts}
                    format={ChartOfAccountsExportFormat.CSV}
                >
                    Chart of Accounts (.csv)
                </ExportDropDownItem>
                <ExportDropDownItem
                    exportFn={exportChartOfAccounts}
                    format={ChartOfAccountsExportFormat.XLSX}
                >
                    Chart of Accounts (.xlsx)
                </ExportDropDownItem>
            </Dropdown.Menu>
        </Dropdown>
    );
};
